import { normalizeAppointmentResourceSelection } from "../pages/calendar/appointmentResourceSelection";
import { calendarCancellationReason } from "./calendarRequestDiagnostics";
import axios from "axios";
import { store } from "../store";
import config from "../config";
import authHeader from "./auth-header";
import moment from "moment";
import { localCalendarAction } from "./calendarRequest";
import { resourceAllocationChoices } from "../helpers/resourceAllocations";
import { withAvailabilityTimeout, CALENDAR_AVAILABILITY_TIMEOUT_MS } from "../utils/availabilityTimeout";

const API_URL = config.API_URL;
const pendingRefundKeys = new Map();
const pendingAppointmentKeys = new Map();
const pendingAppointmentMutationKeys = new Map();
const pendingResourceRequests = new Map();

const resourceRequestOwner = (values = {}) => {
  const availability = values.availebleTimeObj || {};
  const formName = String(availability.formName || values.formName || "appointmentForm");
  const segments = formName.split("/").filter(Boolean);
  if (segments[0] === "appointmentForm" && segments[1]) {
    return `${segments[0]}/${segments[1]}`;
  }
  return segments[0] || "appointmentForm";
};

const resourceRequestSignal = (owner, thunkSignal) => {
  const previous = pendingResourceRequests.get(owner);
  if (previous) previous.abort("superseded");

  const controller = new AbortController();
  const forwardAbort = () => controller.abort(calendarCancellationReason(thunkSignal));
  if (thunkSignal?.aborted) {
    controller.abort(calendarCancellationReason(thunkSignal));
  } else if (thunkSignal) {
    thunkSignal.addEventListener("abort", forwardAbort, { once: true });
  }
  pendingResourceRequests.set(owner, controller);
  return { controller, forwardAbort };
};

const generatedAppointmentKey = () => {
  const cryptoApi = typeof globalThis !== "undefined" ? globalThis.crypto : null;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") return cryptoApi.randomUUID();
  return `appointment-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const appointmentFingerprint = (values = {}) => JSON.stringify(
  Object.keys(values)
    .filter((key) => !["idempotency_key", "idempotencyKey"].includes(key))
    .sort()
    .reduce((result, key) => {
      result[key] = values[key];
      return result;
    }, {}),
);

const appointmentMutationRequest = (operation, values) => {
  const requestedKey = values && (values.idempotency_key || values.idempotencyKey);
  const explicitKey = typeof requestedKey === "string" && requestedKey.trim() ? requestedKey.trim() : null;
  if (explicitKey) return { explicitKey, fingerprint: null };
  const fingerprint = JSON.stringify([operation, appointmentFingerprint(values || {})]);
  const idempotencyKey = pendingAppointmentMutationKeys.get(fingerprint) || generatedAppointmentKey();
  pendingAppointmentMutationKeys.set(fingerprint, idempotencyKey);
  return { explicitKey: null, fingerprint, idempotencyKey };
};

const postAppointmentMutation = (operation, values, action, body, options) => {
  const { explicitKey, fingerprint, idempotencyKey } = appointmentMutationRequest(operation, values);
  const headers = options || {};
  headers["Idempotency-Key"] = explicitKey || idempotencyKey;
  const request = axios.post(API_URL + action, body, { headers });
  if (explicitKey) return request;
  return Promise.resolve(request).then((response) => {
    pendingAppointmentMutationKeys.delete(fingerprint);
    return response;
  });
};

const refundFingerprint = (values) => JSON.stringify(
  Object.keys(values || {})
    .filter((key) => ![
      "idempotency_key",
      "idempotencyKey",
      "paymentIntent",
      "stripe_token_id",
      "stripe_payment_method_id",
    ].includes(key))
    .sort()
    .reduce((result, key) => {
      result[key] = values[key];
      return result;
    }, {}),
);

const generatedRefundKey = () => {
  const cryptoApi = typeof globalThis !== "undefined" ? globalThis.crypto : null;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") return cryptoApi.randomUUID();
  return `refund-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const oneStepRefundResponse = (response) => {
  if (!response || !response.data || typeof response.data !== "object") return response;
  const data = { ...response.data };
  delete data.clientSecret;
  delete data.stripe_payment_method_id;
  delete data.paymentIntent;
  return { ...response, data };
};

const create = (values) => {
  values = normalizeAppointmentResourceSelection(values);
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const formData = new FormData();
  for (let value in values) {
    if (["idempotency_key", "idempotencyKey"].includes(value)) continue;
    if (value === 'override_closed_date') {
      formData.append(value,values[value]===true?'1':'0');
      continue;
    }
    if (["addonservices", "addanotherservices", "service_deposit", "resource_allocations"].includes(value) && values[value] && typeof values[value] === "object") {
      formData.append(value, JSON.stringify(value === "resource_allocations" ? resourceAllocationChoices(values[value]) : values[value]));
    } else {
      formData.append(value, values[value]);
    }
  }
  const action = "afterlogin/appointment/store";
  formData.append("auth_key", auth_key);
  formData.append("action", action);
  const headers = authHeader({ contentType: "multipart/form-data" }) || {};
  const requestedKey = values && (values.idempotency_key || values.idempotencyKey);
  const explicitKey = typeof requestedKey === "string" && requestedKey.trim() ? requestedKey.trim() : null;
  const fingerprint = explicitKey ? null : appointmentFingerprint(values || {});
  const idempotencyKey = explicitKey || pendingAppointmentKeys.get(fingerprint) || generatedAppointmentKey();
  if (!explicitKey) pendingAppointmentKeys.set(fingerprint, idempotencyKey);
  headers["Idempotency-Key"] = idempotencyKey;
  const request = axios.post(API_URL + action, formData, { headers });
  if (explicitKey) return request;
  return Promise.resolve(request).then((response) => {
    pendingAppointmentKeys.delete(fingerprint);
    return response;
  });
};

const update = (values) => {
  values = normalizeAppointmentResourceSelection(values);
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const clickEvent = values && values.clickEvent;
  const formData = new FormData();
  for (let value in values) {
    if (["idempotency_key", "idempotencyKey"].includes(value)) continue;
    if (["addonservices", "addanotherservices", "service_deposit", "resource_allocations"].includes(value) && values[value] && typeof values[value] === "object") {
      formData.append(value, JSON.stringify(value === "resource_allocations" ? resourceAllocationChoices(values[value]) : values[value]));
    } else {
      formData.append(value, values[value]);
    }
  }
  let action = "afterlogin/appointment/update/" + values.id;
  if (clickEvent && clickEvent === "statusupdate") {
    action = "afterlogin/appointment/status/" + values.id;
  }
  formData.append("auth_key", auth_key);
  formData.append("action", action);
  return postAppointmentMutation(
    clickEvent === "statusupdate" ? "status" : "update",
    values,
    action,
    formData,
    authHeader({ contentType: "multipart/form-data" }) || {},
  );
};

const appointmentDepositStore = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const formData = new FormData();
  for (let value in values) {
    formData.append(value, values[value]);
  }
  const action = "afterlogin/appointment/deposit/store";
  formData.append("auth_key", auth_key);
  formData.append("action", action);
  const headers = authHeader({ contentType: "multipart/form-data" }) || {};
  if (values && values.idempotency_key) {
    headers["Idempotency-Key"] = String(values.idempotency_key);
  }
  return axios.post(API_URL + action, formData, { headers });
};

const view = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const sort = values && values.sort;
  const result = values && values.result ? values.result : "";
  const optionType = values && values.optionType ? values.optionType : "";
  //Appointment Calender view parameter
  const start_date = values && values.start_date ? moment.parseZone(values.start_date).format("YYYY-MM-DD HH:mm:ss") : "";
  const end_date = values && values.end_date ? moment.parseZone(values.end_date).format("YYYY-MM-DD HH:mm:ss") : "";
  const timezone = values && values.timezone ? values.timezone : "";
  const type = values && values.type ? values.type : "";
  const staff_id = values && values.staff_id ? values.staff_id : "";
  const resource_item_id = values && values.resource_item_id ? values.resource_item_id : "";
  const resource_id = values && values.resource_id ? values.resource_id : "";
  const formName = values && values.formName ? values.formName : "";
  const showdate = values && values.showdate ? moment(values.showdate).format("YYYY-MM-DD HH:mm:ss") : "";
  const showstartdate = values && values.showstartdate ? moment(values.showstartdate).format("YYYY-MM-DD HH:mm:ss") : "";
  const showenddate = values && values.showenddate ? moment(values.showenddate).format("YYYY-MM-DD HH:mm:ss") : "";

  //Appointment Calender view parameter close
  const filter = values && values.filter ? JSON.stringify(values.filter) : "";
  if (filter === "") {
    store.dispatch({ type: "appointment/closeAppointmentFilter" });
  }
  let sortstring = "";
  if (sort) {
    let sortArray = [];
    Object.keys(sort).map(function (key, index) {
      return (sortArray[index] = `sort[${key}]=${sort[key]}`);
    });
    if (sortArray.length > 0) {
      let jsort = sortArray.join("&");
      sortstring = jsort;
    }
  }
  let pagination = true;
  if (values) {
    if (values.id || start_date) {
      pagination = false;
    }
    if (values.option) {
      pagination = false;
    }
    if (values.pagination == false) {
      pagination = values.pagination;
    }
    if (values.pagination == true) {
      pagination = values.pagination;
    }
  }

  const action = localCalendarAction("afterlogin/appointment/view", values, sortstring);
  const data = {
    auth_key: auth_key,
    action: action,
    pagination: pagination, //true or false
    id: values && values.id ? values.id : "",
    result: result, //business_name,owner_name
    filter: filter, //business_name,owner_name
    option: values && values.option ? values.option : "",
    optionType: optionType,
    client_id: values && values.client_id ? values.client_id : "",
    start_date: start_date,
    end_date: end_date,
    timezone: timezone,
    type: type,
    staff_id: staff_id,
    resource_item_id,
    resource_id,
    formName,
    showdate: showdate,
    showstartdate: showstartdate,
    showenddate: showenddate,
    page_name: values && values.page_name ? values.page_name : "",
    singlestaffaccess: values && values.singlestaffaccess ? values.singlestaffaccess : ""
  };
  return axios.post(API_URL + action, data, {
    headers: authHeader(),
    ...(values?.silent === true ? { silent: true } : {}),
  });
};
const clientappointmentview = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const sort = values && values.sort;
  const result = values && values.result ? values.result : "";
  const optionType = values && values.optionType ? values.optionType : "";
  //Appointment Calender view parameter
  const start_date = values && values.start_date ? moment.parseZone(values.start_date).format("YYYY-MM-DD HH:mm:ss") : "";
  const end_date = values && values.end_date ? moment.parseZone(values.end_date).format("YYYY-MM-DD HH:mm:ss") : "";
  const timezone = values && values.timezone ? values.timezone : "";
  const type = values && values.type ? values.type : "";
  const staff_id = values && values.staff_id ? values.staff_id : "";
  const resource_item_id = values && values.resource_item_id ? values.resource_item_id : "";
  const resource_id = values && values.resource_id ? values.resource_id : "";
  const formName = values && values.formName ? values.formName : "";
  const showdate = values && values.showdate ? moment(values.showdate).format("YYYY-MM-DD HH:mm:ss") : "";
  const showstartdate = values && values.showstartdate ? moment(values.showstartdate).format("YYYY-MM-DD HH:mm:ss") : "";
  const showenddate = values && values.showenddate ? moment(values.showenddate).format("YYYY-MM-DD HH:mm:ss") : "";

  //Appointment Calender view parameter close
  const filter = values && values.filter ? JSON.stringify(values.filter) : "";

  let sortstring = "";
  if (sort) {
    let sortArray = [];
    Object.keys(sort).map(function (key, index) {
      return (sortArray[index] = `sort[${key}]=${sort[key]}`);
    });
    if (sortArray.length > 0) {
      let jsort = sortArray.join("&");
      sortstring = jsort;
    }
  }
  let pagination = true;
  if (values) {
    if (values.id || start_date) {
      pagination = false;
    }
    if (values.option) {
      pagination = false;
    }
    if (values.pagination == false) {
      pagination = values.pagination;
    }
    if (values.pagination == true) {
      pagination = values.pagination;
    }
  }

  const action = localCalendarAction("afterlogin/appointment/view", values, sortstring);
  const data = {
    auth_key: auth_key,
    action: action,
    pagination: pagination, //true or false
    id: values && values.id ? values.id : "",
    result: result, //business_name,owner_name
    filter: filter, //business_name,owner_name
    option: values && values.option ? values.option : "",
    optionType: optionType,
    client_id: values && values.client_id ? values.client_id : "",
    start_date: start_date,
    end_date: end_date,
    timezone: timezone,
    type: type,
    staff_id: staff_id,
    resource_item_id,
    resource_id,
    formName,
    showdate: showdate,
    showstartdate: showstartdate,
    showenddate: showenddate,
    page_name: values && values.page_name ? values.page_name : "",
    singlestaffaccess: values && values.singlestaffaccess ? values.singlestaffaccess : ""
  };
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const deleted = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const action = `afterlogin/appointment/delete/${values.id}`;
  const data = {
    auth_key: auth_key,
    action: action,
  };
  return postAppointmentMutation("delete", values, action, data, authHeader() || {});
};

const reschedule = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  // const clickEvent = values && values.clickEvent;
  const formData = new FormData();
  for (let value in values) {
    if (["idempotency_key", "idempotencyKey"].includes(value)) continue;
    formData.append(value, values[value]);
  }
  // let action = "afterlogin/appointment/reschedule/" + values.id;
  let action = "afterlogin/appointment/reschedule";

  formData.append("auth_key", auth_key);
  formData.append("action", action);
  return postAppointmentMutation(
    "reschedule",
    values,
    action,
    formData,
    authHeader({ contentType: "multipart/form-data" }) || {},
  );
};

const appointmentresize = (values) => {
  const action = "afterlogin/appointment/resize/" + values.id;
  const data = { end_datetime: values.end_datetime,
    ...(values.resize_target ? { resize_target: values.resize_target } : {}) };
  return postAppointmentMutation("resize", values, action, data, authHeader() || {});
};

const appointmentautofillserviceoptions = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const sort = values && values.sort;
  const page = values && values.page;
  const next_page_url = values && values.next_page_url;
  const dropdown = values && values.dropdown ? values.dropdown : "";
  const result = values && values.result ? values.result : "";
  const fromPosition = values && values.fromPosition ? values.fromPosition : "";
  const toPosition = values && values.toPosition ? values.toPosition : "";
  const pagetype = values && values.pagetype ? values.pagetype : "";
  const resource_id = values && values.resource_id ? values.resource_id : "";
  const resource_item_id = values && values.resource_item_id ? values.resource_item_id : "";
  let sortstring = "";
  if (sort) {
    let sortArray = [];
    let sortSubArray = [];
    Object.keys(sort).map(function (key, index) {
      if (Object.keys(sort[key]).length > 0) {
        Object.keys(sort[key]).map(function (subkey, subindex) {
          sortSubArray[subindex] = `sort[${key}.${subkey}]=${sort[key][subkey]}`;
        });
      }
      if (key != "category") {
        sortArray[index] = `sort[${key}]=${sort[key]}`;
      }
    });
    if (sortSubArray.length > 0) {
      let jsubsort = sortSubArray.join("&");
      sortstring = jsubsort;
    }
    if (sortArray.length > 0) {
      let jsort = sortArray.join("&");
      sortstring = jsort;
    }
  }

  if (values) {
    if (values.id) {

    }
    if (values.option) {

    }
    if (values.pagination) {
      void (values.pagination);
    }
  }
  const action = page ? `afterlogin/services/view?page=${page}&${sortstring}` : `afterlogin/services/view?${sortstring}`;
  let servicedata;
  if (dropdown) {
    servicedata = {
      auth_key: auth_key,
      action: action,
      pagination: false, //true or false
      id: "",
      staff_id: values && values.staff_id ? values.staff_id : "",
      // field: "name,description,duration,padding_time,color,service_booked_online,deposit_booked_online,deposit_booked_price", // first_name,last_name,email
      result: result, //business_name,owner_name
      pagetype: pagetype,
      resource_id: resource_id,
      resource_item_id: resource_item_id
    };
  } else {
    servicedata = {
      auth_key: auth_key,
      action: action,
      pagination: false, //true or false
      id: values && values.id ? values.id : "",
      staff_id: values && values.staff_id ? values.staff_id : "",
      // field: values && values.id ? "" : "name,description,duration,padding_time,color,service_booked_online,deposit_booked_online,deposit_booked_price", // first_name,last_name,email
      result: result, //business_name,owner_name
      option: values && values.option ? values.option : "",
      category_id: values && values.category_id ? values.category_id : "",
      fromPosition: fromPosition,
      toPosition: toPosition,
      pagetype: pagetype,
      resource_id: resource_id,
      resource_item_id: resource_item_id
    };
  }

  const data = servicedata;
  return axios.post(next_page_url ? `${next_page_url}&${sortstring}` : API_URL + action, data, { headers: authHeader() });
};

const appointmentpayfail = (values) => {
  const formData = new FormData();
  for (let value in values) {
    if (["error", 'paymentIntent'].includes(value) && values[value] && typeof values[value] === "object") {
      formData.append(value, JSON.stringify(values[value]));
    } else {
      formData.append(value, values[value]);
    }
  }
  const action = `afterlogin/appointment/appointmentpaymentfailed`;
  formData.append("action", action);
  return axios.post(API_URL + action, formData, { headers: authHeader() });
};

const appointmentpayconfirm = (values) => {
  const formData = new FormData();
  for (let value in values) {
    if (["paymentIntent"].includes(value) && values[value] && typeof values[value] === "object") {
      formData.append(value, JSON.stringify(values[value]));
    } else {
      formData.append(value, values[value]);
    }
  }
  const action = `afterlogin/appointment/appointmentpayment`;
  formData.append("action", action);
  return axios.post(API_URL + action, formData, { headers: authHeader() });
};
const refundpaymentstore = (values) => {
  const formData = new FormData();
  for (let value in values) {
    if ([
      "idempotency_key",
      "idempotencyKey",
      "paymentIntent",
      "stripe_token_id",
      "stripe_payment_method_id",
    ].includes(value)) continue;
    if (["paymentIntent", "addonservices", "addanotherservices", "xero_line_allocations"].includes(value) && values[value] && typeof values[value] === "object") {
      formData.append(value, JSON.stringify(values[value]));
    } else {
      formData.append(value, values[value]);
    }
  }
  const action = `afterlogin/appointment/refundstore`;
  formData.append("action", action);
  const requestedKey = values && (values.idempotency_key || values.idempotencyKey);
  const explicitKey = typeof requestedKey === "string" && requestedKey.trim() ? requestedKey.trim() : null;
  const fingerprint = explicitKey ? null : refundFingerprint(values);
  const idempotencyKey = explicitKey || pendingRefundKeys.get(fingerprint) || generatedRefundKey();
  if (!explicitKey) pendingRefundKeys.set(fingerprint, idempotencyKey);
  const request = axios.post(API_URL + action, formData, {
    headers: { ...authHeader(), "Idempotency-Key": idempotencyKey },
  });
  if (explicitKey) return Promise.resolve(request).then(oneStepRefundResponse);
  return Promise.resolve(request).then((response) => {
    pendingRefundKeys.delete(fingerprint);
    return oneStepRefundResponse(response);
  });
};

const refundXeroLines = ({ sale_id, client_id }) => axios.get(
  API_URL + "afterlogin/appointment/refund-xero-lines",
  { headers: authHeader(), params: { sale_id, client_id } },
);

const appointmentresourceitems = (values, thunkAPI = {}) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const sort = values && values.sort;
  const next_page_url = values && values.next_page_url;
  const result = values && values.result ? values.result : "";
  const action = "afterlogin/appointment/resourceitems";
  const sortstring = sort
    ? Object.keys(sort).map((key) => `sort[${key}]=${sort[key]}`).join("&")
    : "";
  const nextPageAction = next_page_url && sortstring
    ? `${next_page_url}${next_page_url.includes("?") ? "&" : "?"}${sortstring}`
    : next_page_url;
  const data = {
    auth_key: auth_key,
    action: action,
    pagination: false,
    service_id: values && values.service_id ? values.service_id : '',
    option: values && values.option ? values.option : "",
    availebleTimeObj: values && values.availebleTimeObj ? values.availebleTimeObj : "",
    result: result,
  };
  const owner = resourceRequestOwner(values);
  const { controller, forwardAbort } = resourceRequestSignal(owner, thunkAPI.signal);
  const request = axios.post(nextPageAction || API_URL + action, data, {
    headers: authHeader(),
    signal: controller.signal,
  });
  return Promise.resolve(request).finally(() => {
    if (pendingResourceRequests.get(owner) === controller) {
      pendingResourceRequests.delete(owner);
    }
    if (thunkAPI.signal) {
      thunkAPI.signal.removeEventListener("abort", forwardAbort);
    }
  });
};

const salecarts = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const result = values && values.result ? values.result : "";
  const action = "afterlogin/sale/appointmentinvoice";
  let data = {
    auth_key: auth_key,
    action: action,
    client_id : values && values.client_id ? values.client_id : '',
    sale_id: values && values.sale_id ? values.sale_id : '',
    salon_id: values && values.salon_id ? values.salon_id : '',
    result: result,
  };
  return axios.post(API_URL + action, data, { headers: authHeader() });
}

const appointmentstaffoptions = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const sort = values && values.sort;
  const page = values && values.page;
  const next_page_url = values && values.next_page_url;
  const result = values && values.result ? values.result : "";
  const dropdown = values && values.dropdown ? values.dropdown : "";
  const service_id = values && values.service_id ? values.service_id : "";
  const pagetype = values && values.pagetype ? values.pagetype : "";
  const getRow = values && values.getRow ? values.getRow : "";

  let sortstring = "";
  if (sort) {
    let sortArray = [];
    let sortSubArray = [];
    Object.keys(sort).map(function (key, index) {
      if (Object.keys(sort[key]).length > 0) {
        Object.keys(sort[key]).map(function (subkey, subindex) {
          sortSubArray[subindex] = `sort[${key}.${subkey}]=${sort[key][subkey]}`;
        });
      }
      if (key != "supplier") {
        sortArray[index] = `sort[${key}]=${sort[key]}`;
      }
    });
    if (sortSubArray.length > 0) {
      let jsubsort = sortSubArray.join("&");
      sortstring = jsubsort;
    }
    if (sortArray.length > 0) {
      let jsort = sortArray.join("&");
      sortstring = jsort;
    }
  }
  let pagination = true;
  if (values) {
    if (values.id) {

    }
    if (values.option) {

    }
    if (values.pagination) {
      void (values.pagination);
    }
    if (values.maxstaffAlert) {

    }
  }
  const action = page ? `afterlogin/staff/view?page=${page}&${sortstring}` : `afterlogin/staff/view?${sortstring}`;
  let staffdata;
  if (dropdown) {
    staffdata = {
      auth_key: auth_key,
      action: action,
      pagination: false, //true or false
      id: "",
      result: result, //business_name,owner_name
      pagetype: pagetype,
      getRow: getRow,
      option: values && values.option ? values.option : "",
      page_name: values && values.page_name ? values.page_name : "",
      singlestaffaccess: values && values.singlestaffaccess ? values.singlestaffaccess : ""
    };
  } else {
    staffdata = {
      auth_key: auth_key,
      action: action,
      pagination: pagination, //true or false
      id: values && values.id ? values.id : "",
      result: result, //business_name,owner_name
      option: values && values.option ? values.option : "",
      service_id: service_id,
      pagetype: pagetype,
      page_name: values && values.page_name ? values.page_name : "",
      singlestaffaccess: values && values.singlestaffaccess ? values.singlestaffaccess : ""
    };
  }
  const data = staffdata;
  return axios.post(next_page_url ? `${next_page_url}&${sortstring}` : API_URL + action, data, { headers: authHeader() });
};

const resourcenextavailable = (values, thunkAPI = {}) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;

  const action = "afterlogin/appointment/nextresourceitemtime";

  const data = {
    auth_key: auth_key,
    action: action,
    ...values,
    resource_allocations: resourceAllocationChoices(values.resource_allocations)
  };
  return withAvailabilityTimeout((signal) => axios.post(API_URL + action, data, { headers: authHeader(), signal }), CALENDAR_AVAILABILITY_TIMEOUT_MS, thunkAPI.signal);
};

const appointmentApiController = {
  create,
  update,
  appointmentDepositStore,
  view,
  deleted,
  reschedule,
  appointmentresize,
  appointmentautofillserviceoptions,
  appointmentpayconfirm,
  appointmentpayfail,
  refundpaymentstore,
  refundXeroLines,
  appointmentresourceitems,
  salecarts,
  appointmentstaffoptions,
  resourcenextavailable,
  clientappointmentview
};
export default appointmentApiController;
